import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Activity, 
  LogIn, 
  FileText, 
  Trash2, 
  Users,
  Search,
  RefreshCw,
  Loader2,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionIcons = {
  login: LogIn,
  service_used: FileText,
  data_deleted: Trash2,
};

const actionLabels = {
  login: 'Anmeldung',
  service_used: 'Service genutzt',
  data_deleted: 'Daten gelöscht',
};

const actionColors = {
  login: 'bg-blue-500/10 text-blue-500',
  service_used: 'bg-green-500/10 text-green-500',
  data_deleted: 'bg-red-500/10 text-red-500',
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/admin/activity-logs?limit=200`),
        axios.get(`${API}/admin/activity-logs/stats`)
      ]);
      setLogs(logsResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Fehler beim Laden der Aktivitätsprotokolle');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.details && log.details.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesService = serviceFilter === 'all' || 
      (serviceFilter === 'login' && log.action === 'login') ||
      (log.service === serviceFilter);
    
    return matchesSearch && matchesService;
  });

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Aktivitätsprotokoll</h1>
            <p className="text-muted-foreground mt-1">
              Übersicht über alle Benutzeraktivitäten
            </p>
          </div>
          <Button onClick={fetchData} variant="outline" data-testid="refresh-logs">
            <RefreshCw className="mr-2 h-4 w-4" />
            Aktualisieren
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <LogIn className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.logins_today}</p>
                    <p className="text-sm text-muted-foreground">Anmeldungen heute</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {stats.service_usage?.reduce((acc, s) => acc + s.count, 0) || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Service-Nutzungen gesamt</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/10 rounded-lg">
                    <Users className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.most_active_users?.length || 0}</p>
                    <p className="text-sm text-muted-foreground">Aktive Benutzer</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Suchen nach Name, E-Mail, Firma..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-logs"
            />
          </div>
          <Select value={serviceFilter} onValueChange={setServiceFilter}>
            <SelectTrigger className="w-[200px]" data-testid="filter-service">
              <SelectValue placeholder="Alle Aktivitäten" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Aktivitäten</SelectItem>
              <SelectItem value="login">Anmeldungen</SelectItem>
              <SelectItem value="mitarbeiterabgleich">Mitarbeiterabgleich</SelectItem>
              <SelectItem value="gesamtabsolvierungen">Gesamtabsolvierungen</SelectItem>
              <SelectItem value="bereinigung_teams">Bereinigung Teams</SelectItem>
              <SelectItem value="csv_transformer">CSV Transformer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Activity Log List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Aktivitäten ({filteredLogs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Keine Aktivitäten gefunden</h3>
                <p className="text-muted-foreground mt-2">
                  {searchTerm || serviceFilter !== 'all' 
                    ? 'Versuchen Sie andere Filterkriterien' 
                    : 'Es wurden noch keine Aktivitäten aufgezeichnet'}
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {filteredLogs.map((log) => {
                    const IconComponent = actionIcons[log.action] || Activity;
                    const colorClass = actionColors[log.action] || 'bg-gray-500/10 text-gray-500';
                    
                    return (
                      <div 
                        key={log.id} 
                        className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`activity-log-${log.id}`}
                      >
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{log.user_name}</span>
                            <span className="text-muted-foreground text-sm">({log.user_email})</span>
                            <Badge variant="outline" className="text-xs">{log.company}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {log.details || actionLabels[log.action] || log.action}
                          </p>
                          {log.service && (
                            <Badge className="mt-2 bg-primary/10 text-primary hover:bg-primary/10">
                              {log.service}
                            </Badge>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDate(log.timestamp)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ActivityLogs;
